import { useMemo } from 'react';

import type { ComponentPageData } from '@/features/content/useComponent';
import type { CourseHomepage, UserRole } from '@/features/courses/useCourse';
import {
  mergeQuestionSettings,
  resolveAllowAiAssistant,
} from '@/features/questions/questionSettingsMerge';

interface ChatAvailabilityParams {
  component?: Pick<ComponentPageData, 'rawComponent'> | null;
  course?: Pick<CourseHomepage, 'courseQuestionSettings'> | null;
  role?: UserRole | null;
}

export function getIsChatAvailable({
  component,
  course,
  role,
}: ChatAvailabilityParams): boolean {
  if (role !== 'student' || !component) {
    return false;
  }

  // The instructor's toggle decides, and it reads the same merged settings the
  // questions do (component over course, per key) rather than a whole-object
  // fallback that let any component settings object shadow the course default.
  // Having an assistant configured on the course no longer forces chat on — it
  // made the toggle inert wherever it mattered most.
  const settings = mergeQuestionSettings(
    course?.courseQuestionSettings,
    component.rawComponent.question_settings ??
      component.rawComponent.questionSettings,
  );

  return resolveAllowAiAssistant(settings);
}

export function useChatAvailability(params: ChatAvailabilityParams) {
  return useMemo(
    () => getIsChatAvailable(params),
    [params.component, params.course?.courseQuestionSettings, params.role],
  );
}
